import { useEffect, useState } from "react";
import { PERMISSIONS, EMPTY_PERMISSIONS } from "./data";
import { api, ApiError, mapApiUser } from "./api";

const EMPTY_FORM = { name: "", username: "", password: "", permissions: { ...EMPTY_PERMISSIONS }, accessGroups: [] };

function UserModal({ groups, initialForm, editing, onCancel, onSave }) {
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  function togglePermission(key) {
    setForm(f => ({ ...f, permissions: { ...f.permissions, [key]: !f.permissions[key] } }));
  }

  function toggleGroup(groupId) {
    setForm(f => ({
      ...f,
      accessGroups: f.accessGroups.includes(groupId)
        ? f.accessGroups.filter(id => id !== groupId)
        : [...f.accessGroups, groupId]
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const name = form.name.trim();
    const username = form.username.trim();
    const password = form.password;
    if (!name || !username || (!editing && !password)) {
      setError(editing ? "نام و نام کاربری الزامی است." : "نام، نام کاربری و رمز عبور الزامی است.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const result = await onSave({
        name,
        username,
        password,
        permissions: form.permissions,
        accessGroups: form.accessGroups
      });
      if (result?.error) setError(result.error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-bg" style={{ display: "block" }} onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <section className="modal" role="dialog" aria-modal="true">
        <h3>{editing ? "ویرایش کاربر" : "افزودن کاربر"}</h3>

        <div className="modal-stack">
          <label className="sr-only" htmlFor="userName">نام کاربر</label>
          <input id="userName" className="input" placeholder="نام کاربر" autoComplete="off"
            value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} autoFocus />

          <label className="sr-only" htmlFor="userUsername">نام کاربری</label>
          <input id="userUsername" className="input" placeholder="نام کاربری (یوزر)" autoComplete="off"
            value={form.username} onChange={(e) => setForm(f => ({ ...f, username: e.target.value }))} />

          <label className="sr-only" htmlFor="userPassword">رمز عبور</label>
          <input id="userPassword" className="input" placeholder={editing ? "رمز عبور جدید (اختیاری)" : "رمز عبور"} autoComplete="off"
            value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} />
        </div>

        <label className="settings-label">دسترسی‌ها</label>
        <div className="access-groups-grid">
          {Object.entries(PERMISSIONS).map(([key, text]) => (
            <label key={key} className="access-group-chip">
              <input type="checkbox" checked={!!form.permissions[key]} onChange={() => togglePermission(key)} />
              <span>{text}</span>
            </label>
          ))}
        </div>

        {form.permissions.manageUsers ? (
          <p className="entity-modal-note">دسترسی «مدیریت کاربران» به‌صورت خودکار همهٔ گروه‌ها را هم شامل می‌شود.</p>
        ) : (
          <>
            <label className="settings-label">دسترسی به گروه‌ها</label>
            <div className="access-groups-grid">
              {groups.map(g => (
                <label key={g.id} className="access-group-chip">
                  <input type="checkbox" checked={form.accessGroups.map(String).includes(String(g.id))} onChange={() => toggleGroup(g.id)} />
                  <span>{g.name}</span>
                </label>
              ))}
            </div>
          </>
        )}

        {error && <div className="login-error">{error}</div>}

        <div className="modal-actions">
          <button className="btn" type="button" onClick={onCancel} disabled={busy}>انصراف</button>
          <button className="btn btn-primary" type="button" onClick={handleSubmit} disabled={busy}>
            {busy ? "در حال ذخیره…" : (editing ? "ذخیره تغییرات" : "افزودن کاربر")}
          </button>
        </div>
      </section>
    </div>
  );
}

export default function UsersPage({ currentUsername, groups, onBack, onUsersChanged }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  async function refresh() {
    setLoading(true);
    try {
      const res = await api.getUsers();
      setUsers((res.data?.users || []).map(mapApiUser));
      if (onUsersChanged) await onUsersChanged();
    } catch (err) {
      setUsers([]);
      window.alert(err instanceof ApiError ? err.message : "خطا در دریافت کاربران");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function openAddModal() {
    setModal({ editing: null, form: { ...EMPTY_FORM, permissions: { ...EMPTY_PERMISSIONS }, accessGroups: [] } });
  }

  function openEditModal(u) {
    setModal({
      editing: u.id,
      form: {
        name: u.name || "",
        username: u.username,
        password: "",
        permissions: { ...EMPTY_PERMISSIONS, ...(u.permissions || {}) },
        accessGroups: Array.isArray(u.accessGroups) ? u.accessGroups : (u.groups || []).map(g => g.id)
      }
    });
  }

  async function handleSave(payload) {
    const body = {
      name: payload.name,
      username: payload.username,
      permissions: { ...EMPTY_PERMISSIONS, ...payload.permissions },
      group_ids: payload.permissions.manageUsers ? [] : payload.accessGroups,
    };
    if (payload.password) body.password = payload.password;

    try {
      if (modal.editing) {
        await api.updateUser(modal.editing, body);
      } else {
        if (!body.password) return { error: "رمز عبور الزامی است." };
        await api.createUser(body);
      }
      await refresh();
      setModal(null);
      return { ok: true };
    } catch (err) {
      return { error: err instanceof ApiError ? err.message : "خطا در ذخیره کاربر" };
    }
  }

  async function handleDelete(u) {
    if (u.username === currentUsername) {
      window.alert("حساب کاربری فعلی خودتان را نمی‌توانید حذف کنید.");
      return;
    }
    if (!window.confirm("کاربر «" + u.username + "» حذف شود؟")) return;
    try {
      await api.deleteUser(u.id);
      await refresh();
    } catch (err) {
      window.alert(err instanceof ApiError ? err.message : "خطا در حذف کاربر");
    }
  }

  function permissionSummary(u) {
    const active = Object.keys(PERMISSIONS).filter(key => u.permissions?.[key]);
    if (!active.length) return "بدون دسترسی خاص (فقط مشاهده)";
    return active.map(key => PERMISSIONS[key]).join(" · ");
  }

  function groupNames(u) {
    if (u.permissions?.manageUsers || u.accessGroups === "all") return "دسترسی به همه گروه‌ها";
    const ids = Array.isArray(u.accessGroups) ? u.accessGroups : (u.groups || []).map(g => g.id);
    const names = groups.filter(g => ids.map(String).includes(String(g.id))).map(g => g.name);
    return names.length ? names.join("، ") : "بدون دسترسی به هیچ گروهی";
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <div className="brand">
          <div className="brand-copy">
            <div className="page-title">مدیریت کاربران</div>
          </div>
        </div>
        <div className="top-actions">
          <button className="btn btn-primary" type="button" onClick={openAddModal}>＋ افزودن کاربر</button>
          <button className="btn btn-soft" type="button" onClick={onBack}>بازگشت به پنل مدیریت</button>
        </div>
      </header>

      <section className="hero">
        <div className="hero-line">
          <div>
            <h1>کاربران سیستم</h1>
            <p>برای هر کاربر نام، نام کاربری، رمز عبور، دسترسی‌های دقیق و گروه‌های قابل مشاهده تعیین کنید.</p>
          </div>
        </div>
      </section>

      <section className="groups">
        {loading && <div className="empty">در حال بارگذاری…</div>}
        {!loading && users.map(u => (
          <article className="person" key={u.id}>
            <div className="user-row">
              <div>
                <div className="name">{u.name || u.username}</div>
                <div className="meta">یوزر: {u.username}</div>
                <div className="meta">{permissionSummary(u)}</div>
                <div className="meta">{groupNames(u)}</div>
              </div>
              <div className="user-actions">
                <button className="small" type="button" onClick={() => openEditModal(u)}>ویرایش</button>
                <button className="small danger" type="button" onClick={() => handleDelete(u)}>حذف</button>
              </div>
            </div>
          </article>
        ))}
        {!loading && !users.length && <div className="empty">کاربری ثبت نشده است.</div>}
      </section>

      {modal && (
        <UserModal
          groups={groups}
          editing={modal.editing}
          initialForm={modal.form}
          onCancel={() => setModal(null)}
          onSave={handleSave}
        />
      )}
    </main>
  );
}
